// frontend/src/pages/NewPatentPage.jsx
// Create new patent project — supports three intake modes:
//   (1) Quick Form   — existing freeform title + description + mindblower
//   (2) Upload Seed  — .md / .txt / .docx / .pdf drag-drop or picker
//   (3) Paste Seed   — big textarea for the canonical PATENT_CONCEPT_SEED format

import { useState, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createProject,
  parseSeedUpload,
  createSeededProject
} from '../utils/api';
import { DarkModeContext } from '../App';

const TABS = [
  { id: 'form', label: 'Quick Form' },
  { id: 'upload', label: 'Upload Seed Document' },
  { id: 'paste', label: 'Paste Seed Text' }
];

const SECTION_LABELS = {
  problem: 'The Problem',
  invention: 'The Invention / Novel Mechanism',
  prior_art_inventor: 'Prior Art — From the Inventor',
  prior_art_external: 'Prior Art — External',
  embodiments: 'Embodiments',
  expected_expansion: 'Expected Areas for Pipeline Expansion',
  non_claims: 'Known Constraints & Non-Claims',
  inventor_intent: 'Inventor Intent'
};

const SECTION_ORDER = [
  'problem',
  'invention',
  'prior_art_inventor',
  'prior_art_external',
  'embodiments',
  'expected_expansion',
  'non_claims',
  'inventor_intent'
];

const ACCEPTED_EXTENSIONS = '.md,.txt,.docx,.pdf';
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export default function NewPatentPage() {
  const [activeTab, setActiveTab] = useState('form');
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Patent Council
              </Link>
            </div>
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
              aria-label="Toggle dark mode"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 inline-block">
          ← Back to dashboard
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">New Patent Project</h1>

        <div role="tablist" aria-label="Intake mode" className="flex border-b border-gray-300 dark:border-gray-700 mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6"
        >
          {activeTab === 'form' && <QuickFormPanel navigate={navigate} />}
          {activeTab === 'upload' && <UploadPanel navigate={navigate} />}
          {activeTab === 'paste' && <PastePanel navigate={navigate} />}
        </div>
      </div>
    </div>
  );
}

function QuickFormPanel({ navigate }) {
  const [inventionTitle, setInventionTitle] = useState('');
  const [inventionDescription, setInventionDescription] = useState('');
  const [mindblowerContext, setMindblowerContext] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const project = await createProject(inventionTitle, inventionDescription, mindblowerContext);
      navigate(`/sessions/${project.id}?autostart=true`);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="inventionTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Invention Title <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <input
          type="text"
          id="inventionTitle"
          required
          value={inventionTitle}
          onChange={(e) => setInventionTitle(e.target.value)}
          className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
          placeholder="e.g., AI-Powered Sensor System for Real-Time Data Processing"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{inventionTitle.length.toLocaleString()} characters</p>
      </div>

      <div>
        <label htmlFor="inventionDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Invention Description <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <textarea
          id="inventionDescription"
          required
          rows={12}
          value={inventionDescription}
          onChange={(e) => setInventionDescription(e.target.value)}
          className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
          placeholder="Provide a detailed description of your invention, including technical implementation, components, and how it works..."
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{inventionDescription.length.toLocaleString()} characters</p>
      </div>

      <div>
        <label htmlFor="mindblowerContext" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Mindblower Private Context (Optional)
        </label>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 mb-2">
          Visible only to the Mindblower (inventor) agent. Use for internal knowledge you do not want shared with adversarial agents.
        </p>
        <textarea
          id="mindblowerContext"
          rows={6}
          value={mindblowerContext}
          onChange={(e) => setMindblowerContext(e.target.value)}
          className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
          placeholder="Optional private context for the inventor agent..."
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{mindblowerContext.length.toLocaleString()} characters</p>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex justify-end space-x-3">
        <Link to="/dashboard" className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </Link>
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Convene the Patent Council'}
        </button>
      </div>
    </form>
  );
}

function UploadPanel({ navigate }) {
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    setParsing(true);
    try {
      const result = await parseSeedUpload(file);
      setParsed(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  if (parsed) {
    return (
      <SeedPreview
        initial={parsed}
        navigate={navigate}
        onReset={() => { setParsed(null); setError(''); }}
      />
    );
  }

  return (
    <div>
      <label
        htmlFor="seedFile"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`block cursor-pointer rounded-lg border-2 border-dashed p-12 text-center ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40'
        }`}
      >
        <input
          ref={inputRef}
          id="seedFile"
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          className="sr-only"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        <div className="text-gray-700 dark:text-gray-300">
          <p className="text-lg font-medium">
            {parsing ? 'Parsing...' : 'Drop a seed document here, or click to select'}
          </p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Accepted: .md, .txt, .docx, .pdf · Max {Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Expected format: PATENT_CONCEPT_SEED with <code className="font-mono">=== SECTION ===</code> delimiters.
          </p>
        </div>
      </label>

      {error && <div className="mt-4"><ErrorBanner message={error} /></div>}
    </div>
  );
}

function PastePanel({ navigate }) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');

  const handleParse = async () => {
    setError('');
    if (!text.trim()) {
      setError('Paste the seed document first.');
      return;
    }
    setParsing(true);
    try {
      const result = await parseSeedUpload(text);
      setParsed(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  };

  if (parsed) {
    return (
      <SeedPreview
        initial={parsed}
        navigate={navigate}
        onReset={() => { setParsed(null); setError(''); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="seedText" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Paste Seed Document
        </label>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 mb-2">
          Paste the full text of a PATENT_CONCEPT_SEED document (canonical format with <code className="font-mono">=== SECTION ===</code> delimiters).
        </p>
        <textarea
          id="seedText"
          rows={20}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full font-mono text-xs border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          placeholder={'# PATENT_CONCEPT_SEED.md\n# Project: ...\n\n===========\nTHE PROBLEM\n===========\n\n...'}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{text.length.toLocaleString()} characters</p>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex justify-end space-x-3">
        <Link to="/dashboard" className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleParse}
          disabled={parsing}
          className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {parsing ? 'Parsing...' : 'Parse & Preview'}
        </button>
      </div>
    </div>
  );
}

function SeedPreview({ initial, navigate, onReset }) {
  const [title, setTitle] = useState(initial.title || '');
  const [sections, setSections] = useState({ ...initial.sections });
  const [inventors, setInventors] = useState(initial.inventors || []);
  const [inventorDraft, setInventorDraft] = useState('');
  const [mindblowerContext, setMindblowerContext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set(['problem', 'invention']));

  const priorFilings = initial.prior_filings || [];
  const missing = initial.missing_sections || [];

  const toggle = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const addInventor = () => {
    const name = inventorDraft.trim();
    if (!name) return;
    if (inventors.includes(name)) return;
    setInventors([...inventors, name]);
    setInventorDraft('');
  };

  const removeInventor = (name) => {
    setInventors(inventors.filter(n => n !== name));
  };

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!sections.problem || !sections.invention) {
      setError('Both "The Problem" and "The Invention" sections are required.');
      return;
    }
    setSubmitting(true);
    try {
      const project = await createSeededProject({
        title: title.trim(),
        seed_document: initial.raw,
        metadata: initial.metadata,
        sections,
        inventors,
        prior_filings: priorFilings,
        mindblower_context: mindblowerContext || null
      });
      navigate(`/sessions/${project.id}?autostart=true`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review parsed seed</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Each section is editable. Adjust anything the parser misread, then convene the council.
          </p>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          ← Use a different seed
        </button>
      </div>

      {missing.length > 0 && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
          Missing required section(s): <strong>{missing.join(', ')}</strong>. Please fill in below before submitting.
        </div>
      )}

      <div>
        <label htmlFor="seedTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Title <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <input
          id="seedTitle"
          type="text"
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Inventors</span>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Parsed from preamble. Add, remove, or correct as needed.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {inventors.map(name => (
            <span key={name} className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-3 py-1 text-sm">
              {name}
              <button
                type="button"
                onClick={() => removeInventor(name)}
                aria-label={`Remove ${name}`}
                className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-100"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={inventorDraft}
            onChange={(e) => setInventorDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInventor(); } }}
            placeholder="Add inventor name and press Enter"
            className="flex-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            onClick={addInventor}
            className="bg-gray-200 dark:bg-gray-700 py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Add
          </button>
        </div>
      </div>

      {priorFilings.length > 0 && (
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Inventor's Prior Filings ({priorFilings.length})
          </span>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Auto-extracted from "Prior Art — From the Inventor". These will be flagged to the adversarial council as the inventor's own prior art.
          </p>
          <ul className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
            {priorFilings.map(f => (
              <li key={f.name} className="px-3 py-2 text-sm">
                <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{f.name}</span>
                <span className="text-gray-600 dark:text-gray-400"> — {f.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Parsed sections</span>
        <div className="space-y-2">
          {SECTION_ORDER.map(key => {
            const present = Boolean(sections[key]);
            const open = expanded.has(key);
            return (
              <div key={key} className="rounded-md border border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  aria-expanded={open}
                  className="w-full px-3 py-2 flex items-center justify-between text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <span>
                    {SECTION_LABELS[key]}
                    {!present && <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">(not found)</span>}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">{open ? '▾' : '▸'}</span>
                </button>
                {open && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-3">
                    <textarea
                      rows={10}
                      value={sections[key] || ''}
                      onChange={(e) => updateSection(key, e.target.value)}
                      className="block w-full font-mono text-xs border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={present ? '' : `No content detected for ${SECTION_LABELS[key]}. Paste content here if needed.`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor="mindblowerContextSeed" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Mindblower Private Context (Optional)
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
          Additional private context for the inventor agent — not shared with adversarial agents.
        </p>
        <textarea
          id="mindblowerContextSeed"
          rows={4}
          maxLength={5000}
          value={mindblowerContext}
          onChange={(e) => setMindblowerContext(e.target.value)}
          className="mt-1 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex justify-end space-x-3">
        <Link to="/dashboard" className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {submitting ? 'Creating...' : 'Convene the Patent Council'}
        </button>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div role="alert" className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
      <div className="text-sm text-red-700 dark:text-red-400">{message}</div>
    </div>
  );
}
